/**
 * Detail collapse for a subject.
 */
import * as React from 'react';
import { BookOpen, Calendar, Info, Star } from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailsCard } from '@/components/common/DetailsCard';
import {
  SUBJECT_STATUS_CONFIG,
  SUBJECT_CYCLE_CONFIG,
  getSubjectStatusColor,
} from '@/components/subjects/subject-configs';
import type { Subject } from '@/types/subject.types';

/** Detail collapse sections for a subject (header + cycle / core / metadata). */
export const renderSubjectDetails = (subject: Subject): React.ReactNode => {
  const statusConfig = SUBJECT_STATUS_CONFIG[subject.status] || SUBJECT_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  const cycleConfig = subject.educationCycle ? SUBJECT_CYCLE_CONFIG[subject.educationCycle] : null;
  const CycleIcon = cycleConfig?.icon;

  const cycleColorName = cycleConfig?.colorName ?? 'color-2';

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <BookOpen className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{subject.label}</h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getSubjectStatusColor(subject.status)}
                variant="filled"
              />
              {cycleConfig && CycleIcon && (
                <DetailBadge
                  label={cycleConfig.label}
                  icon={CycleIcon}
                  colorName={cycleColorName}
                  variant="outlined"
                />
              )}
              {subject.isCore && (
                <DetailBadge
                  label="Matière principale"
                  icon={Star}
                  colorName="color-1"
                  variant="outlined"
                />
              )}
            </div>
            <p className="details-view-header-subtitle">{subject.code}</p>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={Info} title="Informations générales" colorName="color-2" colSpan={2}>
          <div className="space-y-3">
            <DetailField label="Code" value={subject.code} />
            <DetailField label="Libellé" value={subject.label} />
            {subject.description && (
              <DetailField label="Description" value={subject.description} vertical />
            )}
          </div>
        </DetailsCard>

        {subject.createdAt && (
          <DetailsCard icon={Calendar} title="Informations système" colorName="color-1" colSpan={1}>
            <div className="grid grid-cols-2 gap-4">
              <DetailField
                label="Créé le"
                value={new Date(subject.createdAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              />
              {subject.updatedAt && (
                <DetailField
                  label="Modifié le"
                  value={new Date(subject.updatedAt).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                />
              )}
            </div>
          </DetailsCard>
        )}
      </div>
    </>
  );
};
